import { RawVoiceNote } from '@omni/classes/voice-note/voice-note.class';

const TARGET_VALUE = '_indskr_target_value';
const TARGET_LABEL = '_indskr_target_value@OData.Community.Display.V1.FormattedValue';

const CUSTOMER_VALUE = '_indskr_customer_value';
const CUSTOMER_LABEL = '_indskr_customer_value@OData.Community.Display.V1.FormattedValue';
/**
 * The bound entity's logical name comes back as this annotation on the lookup value itself —
 * `indskr_customeridtype` is not a selectable FetchXML/OData attribute on `indskr_note` (verified
 * against a live `dev` row: selecting it, or the platform's own `owneridtype` on the same entity,
 * both fail with "Could not find a property"). Dataverse simply doesn't expose EntityName companion
 * columns this way; the annotation is the only wire representation.
 */
const CUSTOMER_TYPE = '_indskr_customer_value@Microsoft.Dynamics.CRM.lookuplogicalname';

/** `statecode` for an active note. */
const STATE_ACTIVE = 0;

const SERVER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether an id is a Dynamics record id rather than one the device assigned. */
export function isServerId(id: string | null | undefined): boolean {
  return SERVER_ID_PATTERN.test((id ?? '').trim());
}

/**
 * The rep's own active notes, narrowed to what changed since the last sync and,
 * when a meeting is given, to the notes filed against it.
 *
 * @throws When the meeting id is not a Dynamics record id, since it is written into the query.
 */
export function buildNoteFetchXml(lastUpdatedTime: string | null, targetMeetingId?: string): string {
  if (targetMeetingId !== undefined && !isServerId(targetMeetingId)) {
    throw new Error('A meeting id must be a Dynamics record id to filter notes by it.');
  }
  return [
    '<fetch version="1.0" output-format="xml-platform" mapping="logical" no-lock="true" distinct="true">',
    '<entity name="indskr_note">',
    '<attribute name="indskr_noteid" />',
    '<attribute name="indskr_title" />',
    '<attribute name="indskr_body" />',
    '<attribute name="indskr_type" />',
    '<attribute name="indskr_capturedon" />',
    '<attribute name="indskr_externalid" />',
    '<attribute name="indskr_location" />',
    '<attribute name="indskr_latitude" />',
    '<attribute name="indskr_longitude" />',
    '<attribute name="indskr_target" />',
    '<attribute name="indskr_customer" />',
    '<attribute name="statecode" />',
    '<filter>',
    '<condition attribute="ownerid" operator="eq-userid" />',
    `<condition attribute="statecode" operator="eq" value="${STATE_ACTIVE}" />`,
    ...(lastUpdatedTime ? [`<condition attribute="modifiedon" operator="ge" value="${lastUpdatedTime}" />`] : []),
    ...(targetMeetingId ? [`<condition attribute="indskr_target" operator="eq" value="${targetMeetingId.trim()}" />`] : []),
    '</filter>',
    '</entity>',
    '</fetch>',
  ].join('');
}

/**
 * A fetch row as the record stored on disk — the one place the entity's column
 * names are translated into the ones the device keeps.
 *
 * The fetch filters on `statecode eq 0`, so every row it delivers is an active
 * note and none of them arrive converted. A query widened to carry inactive rows
 * would have to say so here, or it would hand back tombstones as live notes.
 */
export function rowToRawNote(row: Record<string, any>): RawVoiceNote {
  return {
    indskr_iovoicenoteid: row.indskr_noteid,
    indskr_name: row.indskr_title ?? null,
    indskr_transcript: row.indskr_body ?? '',
    indskr_notedate: row.indskr_capturedon,
    indskr_convertedactivityid: null,
    externalId: row.indskr_externalid ?? null,
    location: row.indskr_location ?? null,
    latitude: row.indskr_latitude ?? null,
    longitude: row.indskr_longitude ?? null,
    targetMeetingId: row[TARGET_VALUE] ?? null,
    targetMeetingName: row[TARGET_LABEL] ?? null,
    customerId: row[CUSTOMER_VALUE] ?? null,
    customerName: row[CUSTOMER_LABEL] ?? null,
    customerType: row[CUSTOMER_TYPE] ?? null,
  };
}
